import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import { forwardRef, useRef, useState } from "react";
import { useTranslator } from "@templafy/translation";
import { IconView } from "../../icons/IconView.js";
import { IconHidden } from "../../icons/IconHidden.js";
import { BaseTextInput } from "../textInput/internal/BaseTextInput.js";
import { useFieldContext, useSynchronizedProps } from "../form/internal/FieldContext.js";
import { useUUID } from "../../hooks/useUUID.js";
import { VisuallyHidden } from "../visuallyHidden/VisuallyHidden.js";
import { useCombinedRefs } from "../../hooks/useCombinedRefs.js";
const PasswordInput = /*#__PURE__*/ forwardRef(({ testId, className, style, onChange, id: idProperty, disabled: _disabled, readOnly: _readOnly, required: _required, size: _size, status: _status, ...props }, ref)=>{
    const translator = useTranslator();
    const { disabled, readOnly, required, size, status } = useSynchronizedProps({
        disabled: _disabled,
        readOnly: _readOnly,
        required: _required,
        size: _size,
        status: _status,
        inputType: 'text'
    });
    const buttonRef = useRef(null);
    const inputRef = useRef(null);
    const combinedInputRef = useCombinedRefs(inputRef, ref);
    const [isPasswordShown, setIsPasswordShown] = useState(false);
    const fieldContext = useFieldContext();
    const id = useUUID({
        idOverride: idProperty ?? fieldContext?.inputId,
        prefix: 'password'
    });
    return /*#__PURE__*/ jsx(BaseTextInput, {
        type: isPasswordShown ? 'text' : 'password',
        onValueChange: onChange,
        disabled: disabled,
        readOnly: readOnly,
        required: required,
        size: size,
        status: status,
        wrapperProps: {
            testId,
            className,
            style,
            onBlur: (event)=>{
                if (!event.relatedTarget || ![
                    inputRef.current,
                    buttonRef.current
                ].includes(event.relatedTarget)) setIsPasswordShown(false);
            }
        },
        ...props,
        id: id,
        ref: combinedInputRef,
        endAdornment: /*#__PURE__*/ jsxs(Fragment, {
            children: [
                /*#__PURE__*/ jsx(BaseTextInput.AdornmentButton, {
                    disabled: disabled || readOnly,
                    "aria-label": translator.uiComponentsPasswordInputVisibilityButtonLabel({
                        isPasswordShown: isPasswordShown ? 'true' : 'false'
                    }),
                    "aria-controls": id,
                    preventFocusOnClick: false,
                    icon: isPasswordShown ? IconHidden : IconView,
                    onClick: ()=>setIsPasswordShown(!isPasswordShown),
                    ref: buttonRef,
                    testId: "reveal-password"
                }),
                /*#__PURE__*/ jsx(VisuallyHidden, {
                    "aria-live": "polite",
                    "aria-atomic": true,
                    "data-testid": "live-text",
                    children: translator.uiComponentsPasswordInputVisibilityStatus({
                        isPasswordShown: isPasswordShown ? 'true' : 'false'
                    })
                })
            ]
        })
    });
});
PasswordInput.displayName = 'PasswordInput';
export { PasswordInput };
